// chapters/Chapter/index.js - Generic Web Chapter Screen
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Play, CheckCircle, Lock, Clock, BookOpen, Trophy, Star } from 'lucide-react';
import { Button, Card } from '../../components/ui';

const Chapter = ({ chapterId, child, family, onNavigate }) => {
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app';
  
  // Use chapterId from props, fallback to '1' if not provided
  const currentChapterId = chapterId || '1';
  
  // Chapter data for all chapters
  const chapterDataMap = {
    '1': {
      title: "Money Mindset & Goal Setting",
      description: "Plant the seeds of smart money habits and learn to set achievable financial goals",
      icon: "🌱",
      lessons: [
        { id: 1, title: "Understanding Money Mindset", description: "Learn the difference between growth and fixed mindset with money", icon: "🧠", estimatedTime: "10 min" },
        { id: 2, title: "The Power of SMART Goals", description: "Transform vague wishes into specific, achievable financial goals", icon: "🎯", estimatedTime: "15 min" },
        { id: 3, title: "The $500 Challenge Breakdown", description: "See how small amounts add up to meaningful savings", icon: "🧮", estimatedTime: "12 min" },
        { id: 4, title: "Goal Setting Workshop", description: "Create your personal financial goal step by step", icon: "🏆", estimatedTime: "20 min" },
        { id: 5, title: "Mindset Boosters", description: "Daily affirmations and positive money mantras", icon: "⭐", estimatedTime: "8 min" }
      ]
    },
    '2': {
      title: "Budgeting Basics",
      description: "Master the art of tracking income and expenses",
      icon: "💰",
      lessons: [
        { id: 1, title: "What is a Budget?", description: "Understanding the basics of budgeting", icon: "📊", estimatedTime: "12 min" },
        { id: 2, title: "Income vs Expenses", description: "Learn to track money coming in and going out", icon: "⚖️", estimatedTime: "15 min" },
        { id: 3, title: "Creating Your First Budget", description: "Step-by-step budget creation", icon: "📝", estimatedTime: "18 min" },
        { id: 4, title: "Budget Tracking Tools", description: "Apps and methods to track your spending", icon: "📱", estimatedTime: "14 min" },
        { id: 5, title: "Budget Adjustments", description: "How to modify your budget as life changes", icon: "🔄", estimatedTime: "16 min" }
      ]
    },
    '3': {
      title: "Saving Strategies",
      description: "Build emergency funds and develop consistent saving habits",
      icon: "🏦",
      lessons: [
        { id: 1, title: "Why Save Money?", description: "Understanding the importance of saving", icon: "💡", estimatedTime: "10 min" },
        { id: 2, title: "Emergency Fund Basics", description: "Building your financial safety net", icon: "🛡️", estimatedTime: "12 min" },
        { id: 3, title: "Automatic Savings", description: "Set up systems to save without thinking", icon: "🤖", estimatedTime: "15 min" },
        { id: 4, title: "High-Yield Savings", description: "Finding the best places for your money", icon: "📈", estimatedTime: "14 min" },
        { id: 5, title: "Savings Challenges", description: "Fun ways to boost your savings", icon: "🎮", estimatedTime: "11 min" }
      ]
    }
  };

  const chapterData = chapterDataMap[currentChapterId] || chapterDataMap['1'];

  // Load progress from Supabase via API (matches mobile version exactly)
useEffect(() => {
  // Refresh data when we navigate back to this chapter
  if (child?.id) {
    console.log('🔄 Chapter component mounted/updated, refreshing progress...');
    loadCompletions();
  }
}, [child?.id, currentChapterId]);

  const loadCompletions = async () => {
    try {
      console.log('Chapter: Loading completions for child:', child?.id);
      
      if (!child?.id) {
        console.log('Chapter: No child ID provided, skipping progress load');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/lessons/progress/${child.id}`);
      const result = await response.json();
      
      if (result.success) {
        // Filter completions for this specific child (safety measure)
        const childSpecificCompletions = result.data?.filter(completion => {
          const completionChildId = completion.child_id;
          const currentChildId = parseInt(child.id);
          return completionChildId === currentChildId || completionChildId === child.id;
        }) || [];
        
        setCompletions(childSpecificCompletions);
        console.log('Chapter: Loaded completions:', childSpecificCompletions.length);
      } else {
        console.log('Chapter: No completions found or API error');
        setCompletions([]);
      }
    } catch (error) {
      console.error('Chapter: Error loading completions:', error);
      setCompletions([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadCompletions();
    setRefreshing(false);
  };

  // Check if lesson is completed (matches mobile logic)
  const isLessonCompleted = (lessonId) => {
    return completions.some(c => 
      c.chapter_id === parseInt(currentChapterId) && c.lesson_id === lessonId
    );
  };

  // Check if lesson is unlocked (matches mobile logic)
  const isLessonUnlocked = (lessonId) => {
    const chapterInt = parseInt(currentChapterId);
    
    // Chapter 1, Lesson 1 always unlocked
    if (chapterInt === 1 && lessonId === 1) return true;
    
    // Check previous lesson in same chapter
    if (lessonId > 1) {
      return isLessonCompleted(lessonId - 1);
    }
    
    // First lesson of new chapter - check last lesson of previous chapter
    return completions.some(c => 
      c.chapter_id === chapterInt - 1 && c.lesson_id === 5
    );
  };

  // Get lesson status for UI display
  const getLessonStatus = (lessonId) => {
    if (isLessonCompleted(lessonId)) return 'completed';
    if (isLessonUnlocked(lessonId)) return 'unlocked';
    return 'locked';
  };

  // Handle navigation back to program
  const handleBackToProgram = () => {
    if (onNavigate) {
      onNavigate('financialLiteracyProgram', { child: child, family: family });
    }
  };

  // Handle lesson navigation
  const handleLessonClick = (lessonId, status) => {
    if (status === 'locked') {
      // Could show a toast or modal here explaining why it's locked
      console.log('Lesson is locked');
      return;
    }
    
    if (onNavigate) {
      onNavigate('financialLiteracyLesson', { 
        chapterId: currentChapterId, 
        lessonId: lessonId.toString(),
        child: child,
        family: family
      });
    }
  };

  // Calculate chapter progress
  const completedCount = chapterData.lessons.filter(lesson => 
    getLessonStatus(lesson.id) === 'completed'
  ).length;
  const chapterProgress = (completedCount / chapterData.lessons.length) * 100;

  // Get status icon
  const getStatusIcon = (lessonId) => {
    const status = getLessonStatus(lessonId);
    
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'unlocked':
        return <Play className="w-6 h-6 text-blue-500" />;
      case 'locked':
      default:
        return <Lock className="w-6 h-6 text-gray-400" />;
    }
  };

  // Get status styling
  const getStatusStyling = (lessonId) => {
    const status = getLessonStatus(lessonId);
    
    switch (status) {
      case 'completed':
        return 'border-green-200 bg-green-50 hover:bg-green-100';
      case 'unlocked':
        return 'border-blue-200 bg-white hover:bg-blue-50 cursor-pointer';
      case 'locked':
      default:
        return 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed';
    }
  };

  // Get badge styling
  const getBadgeText = (lessonId) => {
    const status = getLessonStatus(lessonId);
    
    switch (status) {
      case 'completed':
        return 'Complete';
      case 'unlocked':
        return 'Ready';
      case 'locked':
      default:
        return 'Locked';
    }
  };

  const getBadgeStyling = (lessonId) => {
    const status = getLessonStatus(lessonId);
    
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'unlocked':
        return 'bg-blue-100 text-blue-800';
      case 'locked':
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading chapter...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50 p-6">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={handleBackToProgram}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Program
          </Button>
          
          <Button
            variant="outline"
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            {refreshing ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            ) : (
              <BookOpen className="w-4 h-4" />
            )}
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {/* Chapter Title & Progress */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4">{chapterData.icon}</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Chapter {currentChapterId}: {chapterData.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
            {chapterData.description}
          </p>
          
          {/* Progress Bar */}
          <div className="max-w-md mx-auto">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Progress</span>
              <span>{Math.round(chapterProgress)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${chapterProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {completedCount} of {chapterData.lessons.length} lessons completed
            </p>
          </div>
        </div>

        {/* Progress Note */}
        <Card className="p-6 mb-8 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <div className="text-2xl">💡</div>
            <div>
              <h3 className="font-semibold text-blue-800 mb-2">How to Progress</h3>
              <p className="text-blue-700">
                Complete lessons in order to unlock the next one and progress through the chapter. 
                Each lesson builds on the previous one to help you master financial literacy concepts.
              </p>
            </div>
          </div>
        </Card>

        {/* Lessons List */}
        <div className="space-y-4 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Lessons</h2>
          
          {chapterData.lessons.map((lesson) => {
            const status = getLessonStatus(lesson.id);
            
            return (
              <Card
                key={lesson.id}
                className={`p-6 transition-all duration-200 ${getStatusStyling(lesson.id)}`}
                onClick={() => handleLessonClick(lesson.id, status)}
              >
                <div className="flex items-start gap-4">
                  
                  {/* Lesson Number & Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-white border-2 border-current flex items-center justify-center text-xl font-bold mb-2">
                      {lesson.id}
                    </div>
                    <div className="text-2xl text-center">
                      {status === 'locked' ? '🔒' : lesson.icon}
                    </div>
                  </div>

                  {/* Lesson Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">
                          Lesson {lesson.id}: {lesson.title}
                        </h3>
                        <p className="text-gray-600 mb-3">
                          {lesson.description}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {lesson.estimatedTime}
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getBadgeStyling(lesson.id)}`}>
                            {getBadgeText(lesson.id)}
                          </span>
                          {status === 'locked' && (
                            <div className="text-gray-400 text-xs">
                              Complete previous lesson to unlock
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Status Icon */}
                      <div className="flex-shrink-0">
                        {getStatusIcon(lesson.id)}
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="mt-4">
                      {status === 'unlocked' && (
                        <Button 
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleLessonClick(lesson.id, status)}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Start Lesson
                        </Button>
                      )}
                      {status === 'completed' && (
                        <Button 
                          variant="outline"
                          className="border-green-200 text-green-700 hover:bg-green-50"
                          onClick={() => handleLessonClick(lesson.id, status)}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Review Lesson
                        </Button>
                      )}
                      {status === 'locked' && (
                        <Button 
                          variant="outline"
                          disabled
                          className="opacity-50 cursor-not-allowed"
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Locked
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Chapter Completion Rewards */}
        <Card className="p-8 bg-gradient-to-r from-green-600 to-blue-600 text-white">
          <div className="text-center">
            <div className="text-4xl mb-4">
              <Trophy className="w-12 h-12 mx-auto mb-2" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Chapter Completion Rewards</h2>
            <p className="text-lg opacity-90 mb-6">
              Complete all lessons to unlock badges and progress to the next chapter!
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-2xl mx-auto">
              {[
                "🧠 Mindset Master",
                "🎯 Goal Getter", 
                "🌱 Digital Native",
                "💪 Action Taker",
                "🌟 Positive Thinker"
              ].map((badge, index) => (
                <div key={index} className="bg-white bg-opacity-20 rounded-lg p-3 text-center">
                  <div className="text-sm font-medium">{badge}</div>
                </div>
              ))}
            </div>
            
            {chapterProgress === 100 ? (
              <div className="mt-6">
                <div className="text-3xl mb-2">🎉</div>
                <p className="text-xl font-semibold">
                  Congratulations! Chapter Complete!
                </p>
                <p className="opacity-90">You're ready for the next chapter!</p>
              </div>
            ) : (
              <p className="text-lg mt-6">
                {chapterData.lessons.length - completedCount} lessons remaining
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Chapter;